import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import {
  PenLine, ScanText, Trophy, Target, ArrowRight, CheckCircle2,
  BookOpen, Sparkles, Quote,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxzdHVkZW50JTIwd3JpdGluZyUyMHN0dWR5aW5nfGVufDB8fHx8MTc4ODA1OTMxMnww&ixlib=rb-4.1.0&q=85";
const DEMO_IMG =
  "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxoYW5kd3JpdHRlbiUyMGRvY3VtZW50JTIwcGFwZXJ8ZW58MHx8fHwxNzg4MDU5MzEyfDA&ixlib=rb-4.1.0&q=85";
const MENTOR_IMG =
  "https://images.unsplash.com/photo-1580894732930-0babd100d356?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxleHBlcmllbmNlZCUyMHRlYWNoZXIlMjBtZW50b3J8ZW58MHx8fHwxNzg4MDU5MzEyfDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FDFDF9] paper-grain">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-5 h-20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-[#2D6A4F] flex items-center justify-center">
            <PenLine className="w-5 h-5 text-[#FDFDF9]" />
          </div>
          <span className="font-display text-2xl tracking-tight">MainsMentor</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-[#525252] hover:text-[#2D6A4F] transition-colors px-3 whitespace-nowrap">
            Log in
          </Link>
          <Link to="/login" data-testid="nav-register">
            <Button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white px-5">Get started</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-10 pb-20 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#2D6A4F] font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> AI Mentor · UPSC · State PSC
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1C1C1C]">
            Get your mains answers evaluated like a <span className="text-[#2D6A4F] italic">seasoned mentor</span> would.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#525252] max-w-xl">
            Upload a photo of your handwritten answer. Our AI mentor reads it, scores it, and gives you the
            honest, detailed feedback of an experienced examiner — for the price of a chai.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/login" data-testid="hero-cta">
              <Button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white px-7 h-12 text-base">
                Evaluate my answer <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <div className="flex items-center gap-2 text-sm text-[#525252]">
              <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" /> 1 free evaluation on signup
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-[#2D6A4F]/8 rounded-[2rem] -rotate-2" />
          <img src={HERO_IMG} alt="Student writing" className="relative rounded-3xl w-full h-[460px] object-cover border border-[#E5E5E5]" />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-[#E5E5E5] shadow-lg p-4 w-52">
            <div className="text-xs uppercase tracking-[0.2em] text-[#525252]">Score</div>
            <div className="font-display text-4xl text-[#2D6A4F]">7.5<span className="text-lg text-[#525252]">/10</span></div>
            <div className="text-xs text-[#525252] mt-1">Structure & analysis improved</div>
          </div>
        </motion.div>
      </section>

      {/* Bento features */}
      <section className="max-w-7xl mx-auto px-5 py-16">
        <div className="mb-12 max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Feedback that actually moves your marks.</h2>
          <p className="mt-3 text-[#525252]">Not a generic score. A full mentor-style correction of every copy.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 bg-white rounded-3xl border border-[#E5E5E5] p-8 hover:-translate-y-1 transition-transform duration-200">
            <img src={DEMO_IMG} alt="Handwritten answer" className="rounded-2xl w-full h-56 object-cover mb-6" />
            <ScanText className="w-7 h-7 text-[#2D6A4F]" />
            <h3 className="mt-3 text-xl font-semibold">Reads your handwriting</h3>
            <p className="mt-2 text-[#525252]">Snap a photo of your written answer — the AI transcribes and understands it exactly as an examiner reads a copy.</p>
          </div>
          <div className="md:col-span-5 bg-[#2D6A4F] text-white rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-200">
            <Target className="w-7 h-7" />
            <h3 className="mt-3 text-xl font-semibold">Dimension-wise scoring</h3>
            <p className="mt-2 text-white/80">Content, structure, language and analytical depth — each scored with specific comments on what to fix.</p>
            <div className="mt-6 space-y-3">
              {["Content & Coverage", "Structure & Presentation", "Analytical Depth"].map((d, i) => (
                <div key={d}>
                  <div className="flex justify-between text-sm text-white/90"><span>{d}</span><span>{[8, 6, 7][i]}/10</span></div>
                  <div className="h-1.5 bg-white/20 rounded-full mt-1"><div className="h-full bg-[#D97757] rounded-full" style={{ width: `${[80, 60, 70][i]}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 bg-white rounded-3xl border border-[#E5E5E5] p-8 hover:-translate-y-1 transition-transform duration-200">
            <Trophy className="w-7 h-7 text-[#D97757]" />
            <h3 className="mt-3 text-xl font-semibold">Topper comparison</h3>
            <p className="mt-2 text-[#525252]">See exactly how a topper would have attempted the same question, and how to bridge the gap.</p>
          </div>
          <div className="md:col-span-4 bg-white rounded-3xl border border-[#E5E5E5] p-8 hover:-translate-y-1 transition-transform duration-200">
            <BookOpen className="w-7 h-7 text-[#2D6A4F]" />
            <h3 className="mt-3 text-xl font-semibold">Model answer</h3>
            <p className="mt-2 text-[#525252]">A concise, structured model answer for every question you submit.</p>
          </div>
          <div className="md:col-span-4 bg-[#FBEEE8] rounded-3xl border border-[#F3D9CE] p-8 hover:-translate-y-1 transition-transform duration-200">
            <Sparkles className="w-7 h-7 text-[#B0452A]" />
            <h3 className="mt-3 text-xl font-semibold text-[#B0452A]">Minimal cost</h3>
            <p className="mt-2 text-[#7c4a3a]">Evaluations start at under ₹10 each. Pay only for what you use — no subscriptions.</p>
          </div>
        </div>
      </section>

      {/* Mentor / how it works */}
      <section className="max-w-7xl mx-auto px-5 py-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative order-2 lg:order-1">
          <div className="absolute -inset-4 bg-[#D97757]/10 rounded-[2rem] rotate-2" />
          <img src={MENTOR_IMG} alt="Experienced mentor" className="relative rounded-3xl w-full h-[420px] object-cover border border-[#E5E5E5]" />
        </div>
        <div className="order-1 lg:order-2">
          <span className="text-xs uppercase tracking-[0.2em] text-[#2D6A4F] font-semibold">How it works</span>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight mt-4">Three steps to a corrected copy.</h2>
          <div className="mt-8 space-y-6">
            {[
              ["Pick your exam & marks", "Choose UPSC, State PSC or generic, and set the marks and word limit."],
              ["Upload your handwritten answer", "Take a clear photo. The AI reads every line like an examiner."],
              ["Get a mentor-grade evaluation", "Score, strengths, weaknesses, model answer and a topper comparison — in under a minute."],
            ].map(([t, d], i) => (
              <div key={t} className="flex gap-4">
                <div className="w-9 h-9 shrink-0 rounded-full bg-[#2D6A4F] text-white flex items-center justify-center font-semibold">{i + 1}</div>
                <div>
                  <h3 className="font-semibold text-lg">{t}</h3>
                  <p className="text-[#525252] mt-1">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote CTA */}
      <section className="max-w-5xl mx-auto px-5 py-20 text-center">
        <Quote className="w-10 h-10 text-[#D97757] mx-auto mb-6" />
        <p className="font-display text-2xl sm:text-3xl leading-snug tracking-tight text-[#1C1C1C]">
          “Answer writing is a skill you build one corrected copy at a time. We just made that correction
          instant, honest and affordable.”
        </p>
        <Link to="/login" className="inline-block mt-10" data-testid="footer-cta">
          <Button className="rounded-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white px-8 h-12 text-base">
            Start evaluating free <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </section>

      <footer className="border-t border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#525252]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#2D6A4F] flex items-center justify-center"><PenLine className="w-3.5 h-3.5 text-white" /></div>
            <span className="font-display text-lg">MainsMentor</span>
          </div>
          <span>AI-assisted evaluation for serious aspirants.</span>
        </div>
      </footer>
    </div>
  );
}
